import React from 'react'
import { Container } from '@mui/material'
import { AccountBalance, ArrowForward, ArrowOutward, AutoAwesome, Business, Diversity3, Handshake, HealthAndSafety, Savings, SendRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Header from '../Components/Header'
import Footer from '../Components/Footer'
import TopHeader from '../Components/TopHeader'
import Contact from '../Components/Contact'
import TitrePage from './TitrePage'
import Reveal from '../Components/ui/Reveal'
import ImageSlot from '../Components/ui/ImageSlot'
import SectionHeader from '../Components/ui/SectionHeader'
import Waves from '../Components/ui/Waves'
import { Images } from '../Data/Images'
import { PRODUCTS, pick as pickLocalized } from '../Data/Products'
import Services from "../Data/Services.json"
import Produits from "../Data/Produits.json"
import Cible from "../Data/Cible.json"
import logo from '../Files/IBG Logo single  white.svg'

function Home() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate()
    const lang = i18n.language === 'fr' ? 0 : 1
    const pick = (value: string) => value.split('|')[lang].trim()
    const targetIcons = [<AccountBalance />, <Savings />, <Diversity3 />, <Business />, <HealthAndSafety />, <Handshake />]
    const featured = React.useMemo(() => PRODUCTS[Math.floor(Math.random() * PRODUCTS.length)], [])

    const go = (link: string) => {
        if (link.startsWith('http')) {
            window.open(link, '_blank', 'noopener')
            return
        }
        navigate(link)
        const target = link.includes('#') ? document.querySelector('#' + link.split('#')[1]) : null
        target ? target.scrollIntoView({ behavior: 'smooth', block: 'start' }) : window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <div className="ibg-page">
            <TitrePage keyWords={"titrePage.accueil.keywords"} lien='/Root' pageDescription={"titrePage.accueil.descriptionPage"} titre='accueil' hiddeTitle />
            <TopHeader />
            <Header />

            <section className="ibg-hero">
                <Waves />
                {Images.hero.src && <img className="ibg-hero__media" src={Images.hero.src} alt={Images.hero.alt} />}
                <div className="ibg-hero__veil" />
                <Container maxWidth="lg" className="ibg-hero__inner">
                    <Reveal>
                        <span className="ibg-eyebrow ibg-eyebrow--light">{t("Home.heroEyebrow")}</span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="ibg-display">{t("Texte.Banniere.titre")}</h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="ibg-hero__lead">{t("Texte.Banniere.contenu")}</p>
                    </Reveal>
                    <Reveal delay={0.3} className="ibg-actions">
                        <button className="ibg-btn ibg-btn--light" onClick={() => go('/#products')}>
                            {t("Home.heroCta")} <ArrowForward fontSize="small" />
                        </button>
                        <button className="ibg-btn ibg-btn--outline-light" onClick={() => go('/contact')}>
                            {t("contactUs")}
                        </button>
                    </Reveal>
                    <div className="ibg-hero__spot">
                        <Reveal delay={0.4}>
                            <button className="ibg-hero__spotCard" onClick={() => go(`/products/${featured.slug}`)}>
                                <span className="ibg-hero__spotEyebrow">{t('Product.spotlight')}</span>
                                <span className="ibg-hero__spotMedia">
                                    <img className={featured.showcase.dark ? 'prod-shot__img prod-shot__img--light' : 'prod-shot__img'} src={featured.showcase.light} alt={featured.showcase.alt} draggable={false} />
                                    {featured.showcase.dark && (
                                        <img className="prod-shot__img prod-shot__img--dark" src={featured.showcase.dark} alt={featured.showcase.alt} draggable={false} />
                                    )}
                                </span>
                                <span className="ibg-hero__spotHead">
                                    <strong>{featured.name}</strong>
                                    <ArrowOutward fontSize="small" />
                                </span>
                                <span className="ibg-hero__spotText">{pickLocalized(featured.tagline, i18n.language)}</span>
                            </button>
                        </Reveal>
                    </div>
                </Container>
                <Container maxWidth="lg" className="ibg-hero__footer">
                    <span className="ibg-hero__label">{t("Home.designedFor")}</span>
                    <ul>
                        {Cible.map((c) => <li key={c.id}>{pick(c.title)}</li>)}
                    </ul>
                </Container>
            </section>

            <section id="about" className="ibg-section">
                <Container maxWidth="lg">
                    <div className="ibg-split">
                        <div className="ibg-split__text">
                            <SectionHeader eyebrow={t("Texte.Accueil.Partie1.titre")} title={t("Texte.Accueil.Partie1.sousTitre")} />
                            <Reveal delay={0.1}>
                                <p className="ibg-body">{t("Texte.Accueil.Partie1.contenu")}</p>
                                <button className="ibg-link" onClick={() => go('/about')}>
                                    {t("Label.Titre.titre9")} <ArrowForward fontSize="small" />
                                </button>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className="ibg-split__media">
                            <ImageSlot image={Images.about} ratio="4 / 5" />
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section className="ibg-section ibg-section--surface">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("Texte.Accueil.Partie2.titre")}
                        title={t("Home.targetsTitle")}
                        description={t("Texte.Accueil.Partie2.sousTitre")}
                    />
                    <div className="ibg-grid ibg-grid--3">
                        {Cible.map((c, index) => (
                            <Reveal key={c.id} delay={index * 0.08} className="ibg-card ibg-card--icon">
                                <span className="ibg-target__icon">{targetIcons[index]}</span>
                                <div className="ibg-card__body">
                                    <h3 className="ibg-h4">{pick(c.title)}</h3>
                                    <p className="ibg-small">{pick(c.provider)}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section id="products" className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("Texte.Accueil.Partie3.titre")}
                        title={t("Home.productsTitle")}
                        description={t("Texte.Accueil.Partie3.sousTitre")}
                    />
                    <div className="ibg-grid ibg-grid--3">
                        {Produits.map((p, index) => (
                            <Reveal key={p.id} delay={index * 0.08}>
                                <article className="ibg-product" onClick={() => go(p.link)}>
                                    <ImageSlot image={Images.products[index]} ratio="16 / 11" label={pick(p.title)} className={p.fit === 'contain' ? 'ibg-image--contain' : ''} />
                                    <div className="ibg-product__body">
                                        {Images.productLogos[index]?.src && (
                                            <img className="ibg-product__logo" src={Images.productLogos[index].src} alt="" />
                                        )}
                                        <div className="ibg-product__head">
                                            <h3 className="ibg-h3">{pick(p.title)}</h3>
                                            <span className="ibg-icon-btn"><ArrowOutward fontSize="small" /></span>
                                        </div>
                                        <p className="ibg-small">{pick(p.description)}</p>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="ibg-section ibg-section--dark">
                <Waves />
                <Container maxWidth="lg">
                    <div className="ibg-split ibg-split--center">
                        <div className="ibg-split__text">
                            <SectionHeader inverted eyebrow={t("Texte.Accueil.Partie4.titre")} title={t("Texte.Accueil.Partie4.titre2")} />
                            <Reveal delay={0.1}>
                                <p className="ibg-body ibg-body--inverted">{t("Texte.Accueil.Partie4.sousTitre")}</p>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className="ibg-split__media">
                            <ImageSlot image={Images.ai} ratio="5 / 4" />
                        </Reveal>
                    </div>
                    <Reveal className="ibg-quote">
                        <p>{t("Texte.Accueil.Partie4.contenu")}</p>
                    </Reveal>
                </Container>
            </section>

            <section id="services" className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("Texte.Accueil.Partie8.titre")}
                        title={t("Home.servicesTitle")}
                        description={t("Texte.Accueil.Partie8.sousTitre")}
                    />
                    <ol className="ibg-list">
                        {Services.map((s, index) => (
                            <Reveal key={s.id} delay={index * 0.05}>
                                <li className="ibg-list__item">
                                    <span className="ibg-list__index">{String(index + 1).padStart(2, '0')}</span>
                                    <h3 className="ibg-h4">{pick(s.title)}</h3>
                                    <p className="ibg-small">{pick(s.provider)}</p>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                </Container>
            </section>

            <section className="ibg-section ibg-section--surface">
                <Container maxWidth="lg">
                    <div className="ibg-split ibg-split--center">
                        <div className="ibg-split__text">
                            <SectionHeader eyebrow={t("Texte.Accueil.Partie10.titre")} title={t("Texte.Accueil.Partie10.sousTitre")} />
                            <Reveal delay={0.1}>
                                <p className="ibg-body">{t("Texte.Accueil.Partie10.contenu")}</p>
                                <div className="ibg-actions">
                                    <button className="ibg-btn ibg-btn--primary" onClick={() => go('/focusia')}>
                                        <AutoAwesome fontSize="small" /> {t("Home.tryFocus")}
                                    </button>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className="ibg-split__media">
                            <div className="ibg-chat">
                                <div className="ibg-chat__head">
                                    <span className="ibg-chat__avatar"><img src={logo} alt="" /></span>
                                    <div>
                                        <strong>Focus IA</strong>
                                        <span className="ibg-chat__status">{t("Home.online")}</span>
                                    </div>
                                </div>
                                <div className="ibg-chat__body">
                                    <div className="ibg-chat__msg ibg-chat__msg--bot">{t("Home.chatGreeting")}</div>
                                    <div className="ibg-chat__msg ibg-chat__msg--user">{t("Home.chatQuestion")}</div>
                                    <div className="ibg-chat__msg ibg-chat__msg--bot">{t("Home.chatAnswer")}</div>
                                </div>
                                <button className="ibg-chat__input" onClick={() => go('/focusia/chat')}>
                                    <span>{t("Home.chatPlaceholder")}</span>
                                    <span className="ibg-chat__send"><SendRounded fontSize="small" /></span>
                                </button>
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section id="contact" className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("Texte.Accueil.Partie9.titre")}
                        title={t("Home.contactTitle")}
                        description={t("Texte.Accueil.Partie9.sousTitre")}
                    />
                    <Contact />
                </Container>
            </section>

            <Footer />
        </div>
    )
}

export default Home
